import React from 'react';
import { Badge } from '../types';
import styles from '../../../../styles/BadgesAndAchievements.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';

interface BadgeCardProps {
  badge: Badge;
  onEdit: (badge: Badge) => void;
  onDelete: (badge: Badge) => void;
  currentUserId?: string;
  isWorkMode?: boolean;
  userBadge?: {
    earned_at?: string;
  };
}

const BadgeCard: React.FC<BadgeCardProps> = ({ badge, onEdit, onDelete, currentUserId, isWorkMode = false, userBadge }) => {
  // Check if current user is the creator of this badge
  const canEditDelete = isWorkMode && currentUserId && badge.created_by?.id === currentUserId;
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const getBadgeTypeColor = (badgeType: string) => {
    const colors: Record<string, string> = {
      points: '#3b82f6',
      tasks: '#10b981',
      streak: '#f59e0b',
      quality: '#8b5cf6',
      participation: '#ec4899',
      special: '#ef4444'
    };
    return colors[badgeType] || '#6b7280';
  };

  const badgeColor = getBadgeTypeColor(badge.badge_type);

  // Personal mode layout
  if (!isWorkMode) {
    return (
      <div 
        className={`${styles.badgeCard} ${styles.personalModeCard}`}
      >
        <div className={styles.personalModePattern}></div>
        <div 
          className={`${styles.personalModeContent} ${styles.tooltipBtn}`}
          data-tooltip={badge.description}
        >
          <div className={styles.personalModeIconWrapper} style={{ backgroundColor: `${badgeColor}20` }}>
            {badge.image_url && badge.image_url.trim() !== '' ? (
              badge.image_url.toLowerCase().endsWith('.svg') ? (
                <img 
                  src={badge.image_url} 
                  alt={badge.name}
                  className={styles.personalModeBadgeImage}
                  style={{ objectFit: 'contain' }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : (
                <img 
                  src={badge.image_url} 
                  alt={badge.name}
                  className={styles.personalModeBadgeImage}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              )
            ) : null}
            <div 
              className={styles.personalModeDefaultIcon} 
              style={{ display: badge.image_url && badge.image_url.trim() !== '' ? 'none' : 'flex' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
                <path d="M2.5.5A.5.5 0 0 1 3 0h10a.5.5 0 0 1 .5.5c0 .538-.012 1.05-.034 1.536a3 3 0 1 1-1.133 5.89c-.79 1.865-1.878 2.777-2.833 3.011v2.173l1.425.356c.194.048.377.135.537.255L13.3 15.1a.5.5 0 0 1-.3.9H3a.5.5 0 0 1-.3-.9l1.838-1.379c.16-.12.343-.207.537-.255L6.813 13.5v-2.173c-.955-.234-2.043-1.146-2.833-3.012a3 3 0 1 1-1.132-5.89A33.076 33.076 0 0 1 2.5.5zm.099 2.54a2 2 0 0 0 .72 3.935c-.333-1.05-.588-2.346-.72-3.935zm10.083 3.935a2 2 0 0 0 .72-3.935c-.133 1.59-.388 2.885-.72 3.935zM8.5 6.866a1 1 0 1 0-1 1.732 1 1 0 0 0 1-1.732zM7.5 9.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm5.5 0a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
              </svg>
            </div>
          </div>
          <div className={styles.personalModeInfo}>
            <h3 className={styles.personalModeTitle}>{badge.name}</h3>
            {userBadge?.earned_at && (
              <div className={styles.personalModeMetadata}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <span>Assigned: {formatDate(userBadge.earned_at)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Work mode layout (existing)
  return (
    <div className={styles.badgeCard}>
      {/* Card Header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.badgeIconWrapper} style={{ backgroundColor: `${badgeColor}20` }}>
            {badge.image_url && badge.image_url.trim() !== '' ? (
              badge.image_url.toLowerCase().endsWith('.svg') ? (
                <img 
                  src={badge.image_url} 
                  alt={badge.name}
                  className={styles.badgeImage}
                  style={{ objectFit: 'contain' }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : (
                <img 
                  src={badge.image_url} 
                  alt={badge.name}
                  className={styles.badgeImage}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              )
            ) : null}
            <div 
              className={styles.defaultBadgeIcon} 
              style={{ display: badge.image_url && badge.image_url.trim() !== '' ? 'none' : 'flex' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                <path d="M2.5.5A.5.5 0 0 1 3 0h10a.5.5 0 0 1 .5.5c0 .538-.012 1.05-.034 1.536a3 3 0 1 1-1.133 5.89c-.79 1.865-1.878 2.777-2.833 3.011v2.173l1.425.356c.194.048.377.135.537.255L13.3 15.1a.5.5 0 0 1-.3.9H3a.5.5 0 0 1-.3-.9l1.838-1.379c.16-.12.343-.207.537-.255L6.813 13.5v-2.173c-.955-.234-2.043-1.146-2.833-3.012a3 3 0 1 1-1.132-5.89A33.076 33.076 0 0 1 2.5.5zm.099 2.54a2 2 0 0 0 .72 3.935c-.333-1.05-.588-2.346-.72-3.935zm10.083 3.935a2 2 0 0 0 .72-3.935c-.133 1.59-.388 2.885-.72 3.935zM8.5 6.866a1 1 0 1 0-1 1.732 1 1 0 0 0 1-1.732zM7.5 9.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm5.5 0a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
              </svg>
            </div>
          </div>
          <div className={styles.cardTitleSection}>
            <h3 className={styles.cardTitle}>{badge.name}</h3>
            <p className={styles.cardDescription}>{badge.description}</p>
          </div>
        </div>
        {canEditDelete && (
          <div className={styles.cardHeaderRight}>
            <div className={styles.cardActions}>
              <button 
                className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnIcon}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(badge);
                }}
                title="Edit badge"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
              <button 
                className={`${buttonStyles.btn} ${buttonStyles.btnDanger} ${buttonStyles.btnIcon}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(badge);
                }}
                title="Delete badge"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3,6 5,6 21,6"></polyline>
                  <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Card Body */}
      <div className={styles.cardBody}>
        <div className={styles.cardTags}>
          <span className={styles.badgeTypeTag} style={{ 
            backgroundColor: `${badgeColor}15`, 
            color: badgeColor,
            borderColor: `${badgeColor}40`
          }}>
            {badge.badge_type_display}
          </span>
          <span className={styles.scopeTag}>
            {badge.scope_display}
          </span>
          {badge.group && (
            <span className={styles.groupTag}>
              {badge.group.name}
            </span>
          )}
          {isWorkMode && (
            <span className={`${styles.statusTag} ${badge.is_active ? styles.statusActive : styles.statusInactive}`}>
              {badge.is_active ? 'Active' : 'Inactive'}
            </span>
          )}
        </div>

    

        <div className={styles.cardMetadata}>
          <div className={styles.metadataItem}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <span>Created: {formatDate(badge.created_at)}</span>
          </div>
          {badge.updated_at && badge.updated_at !== badge.created_at && (
            <div className={styles.metadataItem}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>Updated: {formatDate(badge.updated_at)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BadgeCard;

